import React, { useLayoutEffect, useRef, useState } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { POPOVER_HEIGHT } from '../constants/popover-layout'
import { clampPos, computePopoverPos } from '../helpers/popover-position'

/**
 * Where the popover sits: anchored beside the photo until the user drags it by the grabber, then
 * wherever they put it (clamped to the floating-chrome bounds). Re-anchors on scroll, resize and
 * the panel's own size changes, except mid-drag or mid list-resize.
 */
export function usePopoverPlacement({
  open,
  shape,
  scale,
  canvasRef,
}: {
  open: boolean
  shape: ShapeData
  scale: number
  canvasRef: React.RefObject<HTMLDivElement | null>
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
  const [manualPos, setManualPos] = useState<{ x: number; y: number } | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const isDraggingRef = useRef(false)
  const isListResizingRef = useRef(false)
  const dragOffsetRef = useRef({ x: 0, y: 0 })

  // A closed popover forgets where it was dragged to; the next open anchors to the photo again.
  React.useEffect(() => {
    if (!open) setManualPos(null)
  }, [open, shape.id])

  useLayoutEffect(() => {
    if (!open) {
      setPos(null)

      return
    }

    const update = () => {
      if (isDraggingRef.current || isListResizingRef.current) return

      const el = canvasRef.current

      if (!el) return

      const height = panelRef.current?.offsetHeight ?? POPOVER_HEIGHT

      if (manualPos) {
        setPos(clampPos(manualPos.x, manualPos.y, height))

        return
      }

      setPos(computePopoverPos(shape, scale, el.getBoundingClientRect(), height))
    }

    update()
    const raf = requestAnimationFrame(update)

    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => update()) : null

    if (observer && panelRef.current) observer.observe(panelRef.current)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
      observer?.disconnect()
    }
  }, [open, canvasRef, shape, scale, manualPos])

  const handleDragStart = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!pos) return

    dragOffsetRef.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }
    isDraggingRef.current = true
    setIsDragging(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handleDragMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!isDraggingRef.current) return
    e.preventDefault()

    const height = panelRef.current?.offsetHeight ?? POPOVER_HEIGHT
    const next = clampPos(
      e.clientX - dragOffsetRef.current.x,
      e.clientY - dragOffsetRef.current.y,
      height
    )

    setPos(next)
    setManualPos(next)
  }

  const handleDragEnd = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!isDraggingRef.current) return
    isDraggingRef.current = false
    setIsDragging(false)

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }

  return {
    panelRef,
    pos,
    isDragging,
    isListResizingRef,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
  }
}
